<template>
  <v-container class="app-container--width-max" v-if="hasPrices">
    <BaseAlert
      color="var(--red-100)"
      class="ma-0 ma-md-4 pb-3 border-sm border-solid border-error"
      v-if="quoteFailed"
    >
      <div class="d-flex error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Sorry!</span>
          We couldn't get a quote right now, please try again in a few minutes.
        </div>
      </div>
    </BaseAlert>
    <BaseAlert
      color="var(--red-100)"
      class="ma-0 ma-md-4 pb-3 border-sm border-solid border-error"
      v-if="quoteDeclined"
    >
      <div class="d-flex error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Sorry!</span> Unfortunately no insurer
          was able to offer you a quote at this time based on the details you
          have entered.
        </div>
      </div>
    </BaseAlert>
    <BaseAlert
      color="var(--red-100)"
      class="ma-0 ma-md-4 pb-3 border-sm border-solid border-error"
      v-if="clientError"
    >
      <div class="d-flex error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Sorry!</span> There is a problem with
          the following questions:
          <ul>
            <li
              v-for="(message, index) in clientErrorMessagesArray"
              :key="index"
              class="font-weight-bold"
            >
              {{ message }}
            </li>
          </ul>
        </div>
      </div>
    </BaseAlert>

    <QuoteSummary
      :cover="getCover"
      :sections="sections"
      :section="section"
      @click="CustomiseQuote"
      @showReplacementProductDialog="showReplacementProductDialog"
      heading="Your Home Insurance quotes"
      @showDrawerChangeCover="
        isShowDrawerChangeCover = !isShowDrawerChangeCover
      "
    ></QuoteSummary>

    <DrawerChangeCover
      :coverTypeItems="
        $vuetify.breakpoint.lgAndUp
          ? COVER_TYPE_ITEMS_HOME
          : COVER_TYPE_ITEMS_HOME_SMALL
      "
      v-model="isShowDrawerChangeCover"
    />

    <div class="d-flex pl-4 mt-6 align-center pr-4 flex-wrap">
      <div
        class="d-flex flex-column flex-lg-row flex-wrap gc-5 gr-2"
        v-if="isShowPremiumFlex"
      >
        <div class="d-flex align-center gc-2">
          <h3 class="d-none d-md-block font-20">Premium flex</h3>
          <h3 class="d-md-none font-20">Flex</h3>
          <BaseTooltip
            :text="TOOL_TIP_TEXT.PremiumFlex"
            icon="mdi-help-circle"
          />
        </div>

        <FlexSpinner
          @input="calculateFlex"
          class="font-18"
          type="percentage"
          :maxCommissionSacrifice="getMaxCommissionSacrifice"
          :initialVal="commissionSacrifice"
        />
      </div>
      <div
        class="d-flex flex-column align-sm-end flex-md-row ml-auto order-md-3"
      >
        <BaseButton
          text="Share all"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 d-flex align-center mr-xl-9"
          icon="mdi-share-variant"
          color="#106175"
          small
          @click="shareResults()"
        />
        <BaseButton
          text="Print all"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 d-flex align-center mt-1 mt-md-0"
          icon="mdi-printer"
          color="#106175"
          small
          @click="printAllQuotes()"
        />
      </div>
    </div>

    <TheQuotes
      @shareQuote="shareQuote"
      @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
      @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
      @showEndorsementDetails="showEndorsementDetails"
      @printQuotes="printQuote"
      @setAd="setAd"
      @showPayMonthly="showPayMonthly"
      @showWhatsIncluded="showWhatsIncluded"
    />
    <BadgeDefaqto :show="isShowBadgeDefaqto" />
    <ProductTransferDialog
      ref="productTransfer"
      @setAd="setAd"
      @refreshCover="refreshCover()"
    ></ProductTransferDialog>
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="data?.content"
          :quoteId="data?.quoteId"
          :quoteRequestId="data?.quoteRequestId"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>
    <DrawerCustomiseCover
      v-model="isShowDrawerCustomiseCover"
      :cover="getCover"
      @refreshCover="refreshCover()"
      @editCover="isShowDrawerChangeCover = !isShowDrawerChangeCover"
    ></DrawerCustomiseCover>
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="endorsementDetails"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pt-6 pb-3"
      headerDivider
    >
      <template v-slot:content="{ data }">
        <template v-if="data?.Description">
          <div v-html="data?.Description" class="pa-10"></div>
        </template>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <BaseDialog
      ref="payMonthly"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
    >
      <template v-slot:content>
        <DialogContentPayMonthly />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="whatsIncluded"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      persistent
      :max-width="720"
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedAccidentalDamage
          @closeWhatsIncludedDialog="closeWhatsIncludedDialog"
        />
      </template>
    </BaseDialog>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { QUESTIONS_MODULE, STEPS_MODULE } from "@/store/types/module-types";
import {
  TOOL_TIP_TEXT,
  EXCLUDED,
  CLIENTERROR,
  PRICED,
  FAILED,
  UNABLE,
} from "@/constants/common.constants.js";
import documentService from "@/services/document.service";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { filterSingleQuoteDocs, filterMultiQuoteDocs } from "@/common/docs";
import { adBoolToString, adStringToBool } from "@/common/cover";
import {
  COVER_TYPE_ITEMS_HOME,
  COVER_TYPE_ITEMS_HOME_SMALL,
} from "@/constants/component.constants.js";

export default {
  name: "QuoteResult",
  components: {
    TheQuotes: () => import("@/components/QuoteResult/TheQuotes.vue"),
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    FlexSpinner: () => import("@/components/QuoteResult/FlexSpinner.vue"),
    BadgeDefaqto: () => import("@/components/QuoteResult/BadgeDefaqto.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentPayMonthly: () =>
      import("@/components/QuoteResult/DialogContentPayMonthly"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    ProductTransferDialog: () =>
      import("@/views/Dialog/ProductTransferDialog.vue"),
    QuoteSummary: () => import("@/components/QuoteResult/QuoteSummary.vue"),
    DrawerCustomiseCover: () =>
      import("@/components/QuoteResult/DrawerCustomiseCover.vue"),
    BaseTooltip: () => import("@/components/BaseComponents/BaseTooltip.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    BaseAlert: () => import("@/components/BaseComponents/BaseAlert.vue"),
    DrawerChangeCover: () =>
      import("@/components/QuoteResult/DrawerChangeCover.vue"),
    DialogContentWhatsIncludedAccidentalDamage: () =>
      import(
        "@/components/QuoteResult/DialogContentWhatsIncludedAccidentalDamage"
      ),
  },
  mixins: [PremiumBreakdownMixin],
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    section: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isShowDrawerCustomiseCover: false,
      TOOL_TIP_TEXT,
      documentList: [],
      selectedAds: {
        buildingsAd: EXCLUDED,
        contentsAd: EXCLUDED,
      },
      isShowDrawerChangeCover: false,
      COVER_TYPE_ITEMS_HOME,
      COVER_TYPE_ITEMS_HOME_SMALL,
    };
  },
  watch: {
    getCurrentStep() {
      this.documentList = [];
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getCover",
      "hasPrices",
      "getQuoteRequestId",
      "quoteAccessId",
      "httpConfig",
      "insurerQuotesModel",
      "coverSelection",
      "isShowPremiumFlex",
      "callChannel",
      "getMaxCommissionSacrifice",
      "replacementProductAvailable",
      "clientErrorMessagesArray",
    ]),
    ...mapGetters(STEPS_MODULE, ["getCurrentStep"]),
    ...mapState(QUESTIONS_MODULE, [
      "commissionSacrifice",
      "overlay",
      "lastQuoteErrorType",
      "quoteStatus",
      "isGA4EventToBeFired",
      "userId",
    ]),
    quoteFailed() {
      return this.lastQuoteErrorType === FAILED;
    },
    quoteDeclined() {
      return this.lastQuoteErrorType === UNABLE;
    },
    clientError() {
      return this.lastQuoteErrorType === CLIENTERROR;
    },
    isShowBadgeDefaqto() {
      if (this.isShowDrawerCustomiseCover) return false;
      if (this.isShowDrawerChangeCover) return false;
      return true;
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "updateCommissionSacrifice",
      "setCoverSelection",
      "setOverlayVisible",
      "saveQuoteResults",
      "toggleGAEventFlag",
    ]),
    calculateFlex(val) {
      this.updateCommissionSacrifice(val);
    },
    async shareResults() {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", {
        content: { ...this.getCover, ...this.selectedAds },
        quoteRequestId: this.getQuoteRequestId,
      });
    },
    async shareQuote(quoteId) {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", {
        content: this.getCover,
        quoteId: quoteId,
      });
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    setAd(data) {
      this.selectedAds.buildingsAd = adBoolToString(data.buildingsAd);
      this.selectedAds.contentsAd = adBoolToString(data.contentsAd);
      this.setCoverSelection({
        buildingsAd: data.buildingsAd,
        contentsAd: data.contentsAd,
      });
    },
    printQuote(quote) {
      this.setOverlayVisible(true);
      this.getDocumentList()
        .then(() => {
          const data = {
            ...quote,
            files: this.documentList.filter((doc) =>
              filterSingleQuoteDocs(doc, quote.QuoteId)
            ),
            type: "single",
          };
          this.setOverlayVisible(false);
          this.$refs.printQuotes.open("Download for printing", data);
        })
        .catch((error) => {
          this.setOverlayVisible(false);
          console.error(error);
        });
    },
    printAllQuotes() {
      this.setOverlayVisible(true);
      this.getDocumentList()
        .then(() => {
          const data = {
            type: "multi",
            selectedAds: this.selectedAds,
            files: this.documentList
              .filter((doc) => filterMultiQuoteDocs(doc, this.selectedAds))
              .reverse(),
            quoteRequestId: this.getQuoteRequestId,
          };
          this.setOverlayVisible(false);
          this.$refs.printQuotes.open("Download for printing", data);
        })
        .catch((error) => {
          this.setOverlayVisible(false);
          console.error(error);
        });
    },
    async getDocumentList() {
      await this.saveQuoteResults().catch((e) => {
        throw e;
      });
      if (this.documentList.length) {
        return this.documentList;
      }
      return documentService
        .getDocumentList(
          this.getQuoteRequestId,
          this.callChannel,
          this.httpConfig
        )
        .then((res) => {
          if (res.StatusCode === "OK") {
            res.Documents.forEach((doc) => {
              doc.isDownloading = false;
              doc.isDownloaded = false;
            });

            //The code below is to have a copy of these files for each quote so we could track their download state.
            //only two records of ipid and terms are coming back from api, we need one for each quote for their download state.
            const termFile = res.Documents.find((doc) => doc.Code === "terms");
            const ipidFile = res.Documents.find((doc) => doc.Code === "ipid");
            const factfindsdn = res.Documents.find(
              (doc) => doc.Code === "factfindsdn"
            );
            this.insurerQuotesModel.forEach((quote) => {
              if (quote.isAvailable) {
                quote.quotes.forEach((insurerQuote) => {
                  res.Documents.push({
                    ...termFile,
                    QuoteId: insurerQuote.QuoteId,
                  });
                  res.Documents.push({
                    ...ipidFile,
                    QuoteId: insurerQuote.QuoteId,
                  });
                  res.Documents.push({
                    ...factfindsdn,
                    QuoteId: insurerQuote.QuoteId,
                  });
                });
              }
            });
            this.documentList = res.Documents;
            return this.documentList;
          }
        })
        .catch((error) => {
          throw error;
        });
    },
    CustomiseQuote() {
      if (this.replacementProductAvailable) {
        this.showReplacementProductDialog();
      } else {
        this.isShowDrawerCustomiseCover = true;
      }
    },
    showReplacementProductDialog() {
      this.$refs.productTransfer.open();
    },
    showEndorsementDetails(endorsement) {
      this.$refs.endorsementDetails.open(endorsement.Title, endorsement);
    },
    showPayMonthly() {
      this.$refs.payMonthly.open("More details");
    },
    refreshCover() {
      this.documentList = [];
      const buildingsAd = adStringToBool(this.selectedAds.buildingsAd);
      const contentsAd = adStringToBool(this.selectedAds.contentsAd);
      // Update global state
      this.setCoverSelection({ buildingsAd, contentsAd });
      // Update local models
      this.insurerQuotesModel.forEach((insurer) => {
        insurer.contentsAd = contentsAd;
        insurer.buildingsAd = buildingsAd;
        this.setInsurerQuote(insurer);
      });
    },
    setInsurerQuote(insurer) {
      // Update the local insurer model if it exists (updates screen)
      if (insurer.isAvailable && insurer.quotes) {
        insurer.selectedQuote = insurer.quotes.find(
          (q) =>
            q.BuildingsAD === insurer.buildingsAd &&
            q.ContentsAD === insurer.contentsAd
        );
      }
    },
    showWhatsIncluded() {
      this.$refs.whatsIncluded.open("What's included?");
    },
    closeWhatsIncludedDialog() {
      this.$refs.whatsIncluded.cancel();
    },
  },
  mounted() {
    // Restore accidental damage selections
    this.selectedAds.buildingsAd = adBoolToString(
      this.coverSelection.buildingsAd
    );
    this.selectedAds.contentsAd = adBoolToString(
      this.coverSelection.contentsAd
    );

    if (
      (this.getQuoteRequestId || this.quoteAccessId) &&
      this.quoteStatus === PRICED &&
      this.isGA4EventToBeFired
    ) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "token_login",
        user_id: this.userId,
      });
      this.toggleGAEventFlag(); //Don't fire again when switching between steps
    }
  },
};
</script>

<style lang="scss" scoped>
@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  .additional-content {
    border-top: 1px solid #ccd6dd;
  }
}
</style>
